import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, CreditCard, Loader2, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { getBackendBaseUrl } from "@/lib/config";

const API_URL = getBackendBaseUrl();
const MERCADO_PAGO_LOGO_SRC = "/assets/billing/mercado-pago-logo-horizontal.png";
const MERCADO_PAGO_SECURITY_SCRIPT_ID = "mercado-pago-security-script";

const labels = {
  en: {
    back: "Back",
    title: "Billing & subscription",
    subtitle: "Manage your Voxara plan, provider, upgrades, and renewals.",
    currentPlan: "Current plan",
    noPlan: "Free plan",
    status: "Status",
    renewsOn: "Renews on",
    endsOn: "Ends on",
    manageOnPricing: "View pricing",
    processing: "We are confirming your Stripe checkout...",
    canceled: "Checkout canceled. No changes were applied.",
    current: "Current plan",
    upgrade: "Upgrade",
    downgrade: "Downgrade",
    switchPlan: "Switch plan",
    cancel: "Cancel subscription",
    reactivate: "Reactivate",
    free: "Free",
    loading: "Loading billing details...",
    planUpdated: "Plan updated successfully.",
    checkoutStarted: "Redirecting to Stripe Checkout...",
    cancelScheduled: "Subscription updated successfully.",
    reactivated: "Subscription reactivated.",
    checkoutStatusPaid: "Payment confirmed. Your plan is active.",
    checkoutStatusPending: "Waiting for payment confirmation...",
    checkoutStatusExpired: "Checkout expired. Please try again.",
    paymentError: "Unable to start checkout.",
    paymentPanel: "Dedicated checkout",
    paymentPanelText: "Choose your provider explicitly and complete the subscription here.",
    included: "Included",
    choosePlan: "Choose a plan",
    selected: "Selected",
    selectedPlan: "Selected plan",
    resetSelection: "Choose another plan",
    selectionLockedHint: "To change plan or country, reset the current checkout selection first.",
    provider: "Provider",
    country: "Country",
    stripeLabel: "Stripe (US)",
    mercadoPagoLabel: "Mercado Pago",
    mercadoPagoHint: "Mercado Pago charges in local currency using automatic conversion from the USD base price.",
    mercadoPagoHostedHint: "Subscriptions start on Mercado Pago's secure page. Mercado Pago handles recurring renewals after the user authorizes the first charge.",
    mercadoPagoHostedButton: "Continue with Mercado Pago",
    mercadoPagoHostedRedirecting: "Redirecting to Mercado Pago to authorize your subscription...",
    mercadoPagoHostedPending: "We are waiting for Mercado Pago to confirm your subscription authorization...",
    mercadoPagoHostedCancelled: "The Mercado Pago subscription flow was not completed.",
    stripeHint: "Use Stripe for United States subscriptions billed in USD.",
    chooseProvider: "Choose payment provider",
    createWithStripe: "Continue with Stripe",
    manageWithMercadoPago: "Mercado Pago subscription",
    providerRequired: "Choose a payment provider first.",
    countryRequired: "Choose a country for Mercado Pago.",
    currentProvider: "Current provider",
    currentPrice: "Current charge",
    mercadoPagoCardValidationError: "Mercado Pago could not validate this card. Please verify the card data or try another card.",
    mercadoPagoSandboxCredentialsError: "This checkout is using LIVE Mercado Pago credentials (APP_USR-). To test sandbox payments, switch both Mercado Pago keys to TEST- credentials.",
    mercadoPagoTestUserMismatchError: "Sandbox payments require a Mercado Pago TEST buyer email and matching TEST seller credentials.",
    mercadoPagoRecurringSandboxLimitationError: "Mercado Pago returned 'Card token service not found' for this recurring card checkout. This usually means an account-level Mercado Pago limitation for recurring card tests, even if sandbox credentials are correct.",
    mercadoPagoEnvironmentTitle: "Mercado Pago environment",
    mercadoPagoSandboxReady: "Sandbox ready. Use a Mercado Pago TEST buyer email, cardholder APRO, and document 123456789.",
    mercadoPagoSandboxRecurringNotice: "Important: Mercado Pago recurring card tests can still fail in sandbox with account-level limitations. If you see 'Card token service not found', the issue is usually on Mercado Pago's side, not the form.",
    mercadoPagoLiveWarning: "This preview is still using LIVE Mercado Pago credentials (APP_USR-). Sandbox cards can fail until both Mercado Pago keys are switched to TEST-.",
    mercadoPagoMixedWarning: "Mercado Pago credentials are mixed. Public key and access token must both be TEST- or both APP_USR-.",
    mercadoPagoCredentialPrefix: "Credential prefix",
    accessOnlyProvider: "No active billing subscription",
    accessOnlyHint: "This account has plan access, but no active billing subscription yet. To move to another paid plan, start a new checkout below.",
    mercadoPagoPayerSectionTitle: "Billing identity",
    mercadoPagoPayerSectionHint: "Mercado Pago requires the payer identity before starting the subscription authorization.",
    mercadoPagoPayerEmailLabel: "Billing email",
    mercadoPagoPayerFirstNameLabel: "First name",
    mercadoPagoPayerLastNameLabel: "Last name",
    mercadoPagoIdentificationTypeLabel: "Document type",
    mercadoPagoIdentificationNumberLabel: "Document number",
    mercadoPagoIdentificationNumberPlaceholder: "Enter your document number",
    mercadoPagoPayerValidation: "Complete your billing email, first name, last name, document type, and document number before continuing.",
  },
  es: {
    back: "Volver",
    title: "Facturación y suscripción",
    subtitle: "Administra tu plan de Voxara, provider, upgrades y renovaciones.",
    currentPlan: "Plan actual",
    noPlan: "Plan free",
    status: "Estado",
    renewsOn: "Renueva el",
    endsOn: "Termina el",
    manageOnPricing: "Ver precios",
    processing: "Estamos confirmando tu checkout de Stripe...",
    canceled: "Checkout cancelado. No se aplicaron cambios.",
    current: "Plan actual",
    upgrade: "Subir plan",
    downgrade: "Bajar plan",
    switchPlan: "Cambiar plan",
    cancel: "Cancelar suscripción",
    reactivate: "Reactivar",
    free: "Free",
    loading: "Cargando facturación...",
    planUpdated: "Plan actualizado correctamente.",
    checkoutStarted: "Redirigiendo a Stripe Checkout...",
    cancelScheduled: "Suscripción actualizada correctamente.",
    reactivated: "Suscripción reactivada.",
    checkoutStatusPaid: "Pago confirmado. Tu plan ya está activo.",
    checkoutStatusPending: "Esperando confirmación del pago...",
    checkoutStatusExpired: "El checkout expiró. Intenta nuevamente.",
    paymentError: "No se pudo iniciar el checkout.",
    paymentPanel: "Checkout dedicado",
    paymentPanelText: "Elige explícitamente tu proveedor y completa aquí la suscripción.",
    included: "Incluye",
    choosePlan: "Elige un plan",
    selected: "Seleccionado",
    selectedPlan: "Plan seleccionado",
    resetSelection: "Elegir otro plan",
    selectionLockedHint: "Para cambiar de plan o país, primero reinicia la selección actual del checkout.",
    provider: "Proveedor",
    country: "País",
    stripeLabel: "Stripe (US)",
    mercadoPagoLabel: "Mercado Pago",
    mercadoPagoHint: "Mercado Pago cobra en moneda local usando conversión automática desde el precio base en USD.",
    mercadoPagoHostedHint: "La suscripción se inicia en la página segura de Mercado Pago. Después de la autorización inicial, Mercado Pago gestiona automáticamente las renovaciones.",
    mercadoPagoHostedButton: "Continuar con Mercado Pago",
    mercadoPagoHostedRedirecting: "Redirigiendo a Mercado Pago para autorizar tu suscripción...",
    mercadoPagoHostedPending: "Estamos esperando que Mercado Pago confirme la autorización de tu suscripción...",
    mercadoPagoHostedCancelled: "El flujo de suscripción con Mercado Pago no se completó.",
    stripeHint: "Usa Stripe para suscripciones de Estados Unidos cobradas en USD.",
    chooseProvider: "Elige proveedor de pago",
    createWithStripe: "Continuar con Stripe",
    manageWithMercadoPago: "Suscripción con Mercado Pago",
    providerRequired: "Elige primero un proveedor de pago.",
    countryRequired: "Elige un país para Mercado Pago.",
    currentProvider: "Proveedor actual",
    currentPrice: "Cobro actual",
    mercadoPagoCardValidationError: "Mercado Pago no pudo validar esta tarjeta. Verifica los datos o prueba con otra tarjeta.",
    mercadoPagoSandboxCredentialsError: "Este checkout está usando credenciales LIVE de Mercado Pago (APP_USR-). Para probar pagos sandbox, cambia ambas llaves de Mercado Pago a credenciales TEST-.",
    mercadoPagoTestUserMismatchError: "En sandbox, el pago requiere un email de comprador TEST de Mercado Pago y credenciales TEST del seller que coincidan.",
    mercadoPagoRecurringSandboxLimitationError: "Mercado Pago devolvió 'Card token service not found' para esta suscripción recurrente con tarjeta. Normalmente esto indica una limitación a nivel de cuenta en Mercado Pago para pruebas recurrentes, incluso si las credenciales sandbox son correctas.",
    mercadoPagoEnvironmentTitle: "Entorno de Mercado Pago",
    mercadoPagoSandboxReady: "Sandbox listo. Usa un email buyer TEST de Mercado Pago, titular APRO y documento 123456789.",
    mercadoPagoSandboxRecurringNotice: "Importante: las pruebas recurrentes con tarjeta en Mercado Pago pueden seguir fallando en sandbox por limitaciones de cuenta. Si ves 'Card token service not found', normalmente el problema es de Mercado Pago, no del formulario.",
    mercadoPagoLiveWarning: "Este preview todavía usa credenciales LIVE de Mercado Pago (APP_USR-). Las tarjetas sandbox pueden fallar hasta cambiar ambas llaves de Mercado Pago a TEST-.",
    mercadoPagoMixedWarning: "Las credenciales de Mercado Pago están mezcladas. La public key y el access token deben ser ambos TEST- o ambos APP_USR-.",
    mercadoPagoCredentialPrefix: "Prefijo de credencial",
    accessOnlyProvider: "Sin suscripción activa de cobro",
    accessOnlyHint: "Esta cuenta tiene acceso al plan, pero todavía no una suscripción activa de cobro. Para pasar a otro plan pago, inicia un nuevo checkout abajo.",
    mercadoPagoPayerSectionTitle: "Datos del pagador",
    mercadoPagoPayerSectionHint: "Mercado Pago requiere la identificación del pagador antes de iniciar la autorización de la suscripción.",
    mercadoPagoPayerEmailLabel: "Email de facturación",
    mercadoPagoPayerFirstNameLabel: "Nombres",
    mercadoPagoPayerLastNameLabel: "Apellidos",
    mercadoPagoIdentificationTypeLabel: "Tipo de documento",
    mercadoPagoIdentificationNumberLabel: "Número de documento",
    mercadoPagoIdentificationNumberPlaceholder: "Ingresa tu número de documento",
    mercadoPagoPayerValidation: "Completa el email, nombres, apellidos, tipo de documento y número de documento antes de continuar.",
  },
};

const billingPlanContent = {
  starter: {
    summary: {
      es: "Para empezar: transcribe, resume y detecta momentos clave. Chat con IA solo en documentos y PDF.",
      en: "To get started: transcribe, summarize, and detect key moments. AI chat for documents and PDFs only.",
    },
    features: {
      es: [
        { text: "10 horas de video / día", included: true },
        { text: "Transcripción completa", included: true },
        { text: "Detección de momentos clave", included: true },
        { text: "Resumen con IA", included: true },
        { text: "Exportar TXT y SRT", included: true },
        { text: "Soporte estándar", included: true },
        { text: "Procesamiento prioritario", included: false },
        { text: "Uso para equipos y firmas", included: false },
      ],
      en: [
        { text: "10 hours of video / day", included: true },
        { text: "Full transcription", included: true },
        { text: "Key moment detection", included: true },
        { text: "AI Summary", included: true },
        { text: "Export TXT & SRT", included: true },
        { text: "Standard support", included: true },
        { text: "Priority processing", included: false },
        { text: "Team & firm use", included: false },
      ],
    },
  },
  professional: {
    summary: {
      es: "El más popular: más horas, chat con IA en videos y audios, prioridad en procesamiento y soporte.",
      en: "Most popular: more hours, AI chat for videos and audio, priority processing and support.",
    },
    features: {
      es: [
        { text: "15 horas de video / día", included: true },
        { text: "Transcripción completa", included: true },
        { text: "Detección de momentos clave", included: true },
        { text: "Resumen con IA", included: true },
        { text: "Exportar TXT, SRT y JSON", included: true },
        { text: "Procesamiento prioritario", included: true },
        { text: "Resúmenes detallados", included: true },
        { text: "Soporte prioritario", included: true },
      ],
      en: [
        { text: "15 hours of video / day", included: true },
        { text: "Full transcription", included: true },
        { text: "Key moment detection", included: true },
        { text: "AI Summary", included: true },
        { text: "Export TXT, SRT & JSON", included: true },
        { text: "Priority processing", included: true },
        { text: "Detailed summaries", included: true },
        { text: "Priority support", included: true },
      ],
    },
  },
  enterprise: {
    summary: {
      es: "Para equipos y firmas: horas ilimitadas, controles administrativos y soporte premium.",
      en: "For teams and firms: unlimited hours, admin controls, and premium support.",
    },
    features: {
      es: [
        { text: "Horas de video ilimitadas", included: true },
        { text: "Transcripción completa", included: true },
        { text: "Detección de momentos clave", included: true },
        { text: "Resúmenes detallados", included: true },
        { text: "Todos los formatos de exportación", included: true },
        { text: "Uso para equipos y firmas", included: true },
        { text: "Controles administrativos", included: true },
        { text: "Soporte premium", included: true },
      ],
      en: [
        { text: "Unlimited video hours", included: true },
        { text: "Full transcription", included: true },
        { text: "Key moment detection", included: true },
        { text: "Detailed summaries", included: true },
        { text: "All export formats", included: true },
        { text: "Team & firm use", included: true },
        { text: "Admin controls", included: true },
        { text: "Premium support", included: true },
      ],
    },
  },
};

const formatDate = (value, language) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "es" ? "es-ES" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
};

const formatSubscriptionStatus = (status, language, fallbackLabel) => {
  if (!status) return fallbackLabel;

  const normalizedStatus = String(status).trim().toLowerCase();
  const statusLabels = {
    active: language === "es" ? "Activa" : "Active",
    authorized: "Authorized",
    cancelled: language === "es" ? "Cancelada" : "Cancelled",
    pending: language === "es" ? "Pendiente" : "Pending",
    paused: language === "es" ? "Pausada" : "Paused",
    inactive: language === "es" ? "Inactiva" : "Inactive",
    free: fallbackLabel,
  };

  return statusLabels[normalizedStatus] || status.charAt(0).toUpperCase() + status.slice(1);
};

const getSubscriptionStatusClasses = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();

  if (["authorized", "active"].includes(normalizedStatus)) {
    return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
  }

  if (["cancelled", "inactive"].includes(normalizedStatus)) {
    return "border-red-500/40 bg-red-500/10 text-red-300";
  }

  if (["pending", "paused"].includes(normalizedStatus)) {
    return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  }

  return "border-border bg-muted/40 text-muted-foreground";
};

const formatProviderName = (provider, fallbackLabel) => {
  if (!provider) return fallbackLabel;

  const normalizedProvider = String(provider).trim().toLowerCase();
  const providerLabels = {
    mercadopago: "Mercado Pago",
    stripe: "Stripe",
  };

  return providerLabels[normalizedProvider] || provider;
};

const MERCADO_PAYER_IDENTIFICATION_OPTIONS = {
  CO: [
    { value: "CC", label: "CC" },
    { value: "CE", label: "CE" },
    { value: "NIT", label: "NIT" },
    { value: "TI", label: "TI" },
    { value: "PASSPORT", label: "Pasaporte" },
  ],
  AR: [
    { value: "DNI", label: "DNI" },
    { value: "CUIL", label: "CUIL" },
    { value: "CUIT", label: "CUIT" },
    { value: "PASSPORT", label: "Pasaporte" },
  ],
  BR: [
    { value: "CPF", label: "CPF" },
    { value: "CNPJ", label: "CNPJ" },
  ],
  CL: [
    { value: "RUT", label: "RUT" },
    { value: "PASSPORT", label: "Pasaporte" },
  ],
  MX: [
    { value: "CURP", label: "CURP" },
    { value: "RFC", label: "RFC" },
    { value: "PASSPORT", label: "Pasaporte" },
  ],
  PE: [
    { value: "DNI", label: "DNI" },
    { value: "CE", label: "CE" },
    { value: "RUC", label: "RUC" },
    { value: "PASSPORT", label: "Pasaporte" },
  ],
};

const normalizeMercadoIdentificationType = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
const normalizeMercadoIdentificationNumber = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 40);
const getMercadoIdentificationOptions = (countryCode) => MERCADO_PAYER_IDENTIFICATION_OPTIONS[countryCode] || MERCADO_PAYER_IDENTIFICATION_OPTIONS.CO;

export default function BillingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const t = labels[language] || labels.en;

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("muted");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("CO");
  const [mercadoLoading, setMercadoLoading] = useState(false);
  const [mercadoDeviceSessionId, setMercadoDeviceSessionId] = useState("");
  const [mercadoPayerForm, setMercadoPayerForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    identificationType: "CC",
    identificationNumber: "",
  });
  const handledCheckoutSessionsRef = useRef(new Set());

  const loadMercadoPagoSecurityScript = useCallback(() => {
    if (typeof window === "undefined") return Promise.resolve();

    const existingScript = document.getElementById(MERCADO_PAGO_SECURITY_SCRIPT_ID);
    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Mercado Pago security script failed to load")), { once: true });
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.id = MERCADO_PAGO_SECURITY_SCRIPT_ID;
      script.src = "https://www.mercadopago.com/v2/security.js";
      script.async = true;
      script.setAttribute("view", "checkout");
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = () => reject(new Error("Mercado Pago security script failed to load"));
      document.head.appendChild(script);
    });
  }, []);

  const getMercadoPagoDeviceSessionId = useCallback(async () => {
    if (typeof window === "undefined") return null;
    if (mercadoDeviceSessionId) return mercadoDeviceSessionId;

    await loadMercadoPagoSecurityScript();

    for (let attempt = 0; attempt < 15; attempt += 1) {
      const currentDeviceSessionId = typeof window.MP_DEVICE_SESSION_ID === "string" ? window.MP_DEVICE_SESSION_ID.trim() : "";
      if (currentDeviceSessionId) {
        setMercadoDeviceSessionId(currentDeviceSessionId);
        return currentDeviceSessionId;
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    return null;
  }, [loadMercadoPagoSecurityScript, mercadoDeviceSessionId]);

  const getBillingErrorMessage = useCallback((error) => {
    const detail = error?.response?.data?.detail;
    const detailCode = typeof detail === "object" && detail ? detail.code : null;
    const detailMessage = typeof detail === "object" && detail ? detail.message : detail;

    if (
      detailCode === "MERCADOPAGO_SANDBOX_CREDENTIALS_REQUIRED" ||
      (typeof detailMessage === "string" && detailMessage.includes("LIVE credentials (APP_USR-)"))
    ) {
      return t.mercadoPagoSandboxCredentialsError;
    }

    if (detailCode === "MERCADOPAGO_TEST_USER_MISMATCH") {
      return t.mercadoPagoTestUserMismatchError;
    }

    if (detailCode === "MERCADOPAGO_RECURRING_SANDBOX_LIMITATION") {
      return t.mercadoPagoRecurringSandboxLimitationError;
    }

    if (
      detailCode === "CC_VAL_433" ||
      (typeof detailMessage === "string" &&
        (detailMessage.includes("CC_VAL_433") || detailMessage.toLowerCase().includes("credit card validation has failed")))
    ) {
      return t.mercadoPagoCardValidationError;
    }

    return detailMessage || t.paymentError;
  }, [t.mercadoPagoCardValidationError, t.paymentError]);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const sessionId = query.get("session_id");
  const paymentId = query.get("payment_id") || query.get("collection_id");
  const canceled = query.get("canceled");
  const queryProvider = query.get("provider");
  const preselectedPlan = query.get("plan") || query.get("plan_id");
  const preselectedCountry = query.get("country");

  const clearCheckoutQueryParams = useCallback(() => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.delete("session_id");
    nextQuery.delete("payment_id");
    nextQuery.delete("collection_id");
    nextQuery.delete("collection_status");
    nextQuery.delete("status");
    nextQuery.delete("external_reference");
    nextQuery.delete("merchant_order_id");
    nextQuery.delete("preference_id");
    nextQuery.delete("canceled");

    navigate(
      {
        pathname: location.pathname,
        search: nextQuery.toString() ? `?${nextQuery.toString()}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const loadData = useCallback(async () => {
    const [plansResponse, subscriptionResponse, providersResponse] = await Promise.all([
      axios.get(`${API_URL}/api/subscription/plans`),
      axios.get(`${API_URL}/api/subscription/status`),
      axios.get(`${API_URL}/api/subscription/providers`),
    ]);
    setPlans(plansResponse.data.plans || []);
    setSubscription(subscriptionResponse.data);
    setProviders(providersResponse.data);
  }, []);

  const syncCheckout = useCallback(async () => {
    if (!sessionId) return;
    if (handledCheckoutSessionsRef.current.has(sessionId)) return;

    setStatusMessage(queryProvider === "mercadopago" ? t.mercadoPagoHostedPending : t.processing);
    setStatusTone("muted");

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const response = await axios.get(`${API_URL}/api/subscription/checkout-status/${sessionId}`, {
        params: paymentId ? { payment_id: paymentId } : undefined,
      });
      const paymentStatus = response.data.payment_status;
      const checkoutStatus = response.data.status;

      if (paymentStatus === "paid" || paymentStatus === "authorized" || paymentStatus === "active") {
        handledCheckoutSessionsRef.current.add(sessionId);
        setStatusMessage(t.checkoutStatusPaid);
        setStatusTone("success");
        await refreshUser();
        await loadData();
        clearCheckoutQueryParams();
        return;
      }

      if (paymentStatus === "cancelled") {
        handledCheckoutSessionsRef.current.add(sessionId);
        setStatusMessage(t.mercadoPagoHostedCancelled);
        setStatusTone("error");
        await loadData();
        clearCheckoutQueryParams();
        return;
      }

      if (checkoutStatus === "expired") {
        handledCheckoutSessionsRef.current.add(sessionId);
        setStatusMessage(t.checkoutStatusExpired);
        setStatusTone("error");
        await loadData();
        clearCheckoutQueryParams();
        return;
      }

      setStatusMessage(queryProvider === "mercadopago" ? t.mercadoPagoHostedPending : t.checkoutStatusPending);
      setStatusTone("muted");
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }, [clearCheckoutQueryParams, loadData, paymentId, queryProvider, refreshUser, sessionId, t.checkoutStatusExpired, t.checkoutStatusPaid, t.checkoutStatusPending, t.mercadoPagoHostedCancelled, t.mercadoPagoHostedPending, t.processing]);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    if (sessionId && queryProvider === "mercadopago") {
      navigate(
        {
          pathname: "/checkout/mercadopago/success",
          search: location.search,
        },
        { replace: true }
      );
      return;
    }

    const init = async () => {
      setLoading(true);
      try {
        await loadData();
        if (sessionId) {
          await syncCheckout();
        } else if (canceled) {
          setStatusMessage(t.canceled);
          setStatusTone("error");
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [canceled, loadData, location.search, navigate, queryProvider, sessionId, syncCheckout, t.canceled, user]);

  useEffect(() => {
    if (preselectedPlan) setSelectedPlan(preselectedPlan);
    if (preselectedCountry) setSelectedCountry(preselectedCountry);
  }, [preselectedCountry, preselectedPlan]);

  useEffect(() => {
    if (subscription?.payment_provider === "mercadopago" && subscription?.provider_country_code) {
      setSelectedCountry(subscription.provider_country_code);
      return;
    }
    if (!providers) return;
    if (!preselectedCountry && providers.mercadopago_default_country_code) {
      setSelectedCountry(providers.mercadopago_default_country_code);
    }
  }, [preselectedCountry, providers, subscription]);

  useEffect(() => {
    loadMercadoPagoSecurityScript().catch(() => {
      // Best effort only; checkout can still proceed without blocking the user.
    });
  }, [loadMercadoPagoSecurityScript]);

  const mercadoIdentificationOptions = useMemo(
    () => getMercadoIdentificationOptions(selectedCountry),
    [selectedCountry]
  );

  useEffect(() => {
    const fallbackType = mercadoIdentificationOptions[0]?.value || "CC";
    setMercadoPayerForm((currentForm) => {
      const userType = normalizeMercadoIdentificationType(user?.mercadopago_identification_type);
      const resolvedType = [currentForm.identificationType, userType, fallbackType].find((candidate) =>
        mercadoIdentificationOptions.some((option) => option.value === candidate)
      ) || fallbackType;

      return {
        email: currentForm.email || user?.mercadopago_payer_email || user?.email || "",
        firstName: currentForm.firstName || user?.first_name || "",
        lastName: currentForm.lastName || user?.last_name || "",
        identificationType: resolvedType,
        identificationNumber: currentForm.identificationNumber || user?.mercadopago_identification_number || "",
      };
    });
  }, [mercadoIdentificationOptions, user]);

  const updateMercadoPayerField = useCallback((field, value) => {
    setMercadoPayerForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }, []);

  const currentPrice = useMemo(() => {
    if (!subscription?.plan_id) return 0;
    return plans.find((plan) => plan.id === subscription.plan_id)?.price || 0;
  }, [plans, subscription]);

  const hasBillingSubscription = Boolean(subscription?.has_subscription);
  const hasBlockingBillingSubscription = Boolean(subscription?.has_subscription && !["cancelled", "free"].includes(subscription?.status || ""));
  const hasPlanAccess = Boolean(subscription?.plan_id && subscription?.plan_id !== "free");
  const accessOnly = Boolean(hasPlanAccess && !hasBillingSubscription);
  const subscriptionStatusLabel = formatSubscriptionStatus(subscription?.status, language, t.free);
  const subscriptionStatusClasses = getSubscriptionStatusClasses(subscription?.status);
  const providerLabel = formatProviderName(subscription?.payment_provider, accessOnly ? t.accessOnlyProvider : "—");

  const selectedPlanData = useMemo(
    () => plans.find((plan) => plan.id === selectedPlan) || null,
    [plans, selectedPlan]
  );
  const selectedPlanContent = selectedPlanData ? billingPlanContent[selectedPlanData.id] || null : null;

  const mercadoQuote = useMemo(() => {
    if (!providers?.mercadopago?.quotes || !selectedPlan || !selectedCountry) return null;
    return providers.mercadopago.quotes[selectedCountry]?.[selectedPlan] || null;
  }, [providers, selectedCountry, selectedPlan]);

  const checkoutFormVisible = useMemo(
    () => !hasBlockingBillingSubscription && Boolean(selectedPlan && providers?.mercadopago?.enabled && mercadoQuote),
    [hasBlockingBillingSubscription, mercadoQuote, providers?.mercadopago?.enabled, selectedPlan]
  );

  const startMercadoPagoCheckout = useCallback(async () => {
    if (!selectedPlan) {
      setStatusMessage(t.choosePlan);
      setStatusTone("error");
      return;
    }

    const payerProfile = {
      email: String(mercadoPayerForm.email || "").trim().toLowerCase(),
      firstName: String(mercadoPayerForm.firstName || "").trim(),
      lastName: String(mercadoPayerForm.lastName || "").trim(),
      identificationType: normalizeMercadoIdentificationType(mercadoPayerForm.identificationType),
      identificationNumber: normalizeMercadoIdentificationNumber(mercadoPayerForm.identificationNumber),
    };

    if (
      !payerProfile.email ||
      payerProfile.firstName.length < 2 ||
      payerProfile.lastName.length < 2 ||
      payerProfile.identificationType.length < 2 ||
      payerProfile.identificationNumber.length < 5
    ) {
      setStatusMessage(t.mercadoPagoPayerValidation);
      setStatusTone("error");
      return;
    }

    setMercadoLoading(true);
    try {
      setStatusMessage(t.mercadoPagoHostedRedirecting);
      setStatusTone("muted");
      const deviceSessionId = await getMercadoPagoDeviceSessionId();
      const response = await axios.post(`${API_URL}/api/subscription/checkout`, {
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        provider: "mercadopago",
        country_code: selectedCountry,
        payer_email: payerProfile.email,
        payer_first_name: payerProfile.firstName,
        payer_last_name: payerProfile.lastName,
        payer_identification_type: payerProfile.identificationType,
        payer_identification_number: payerProfile.identificationNumber,
        device_session_id: deviceSessionId || undefined,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      setStatusMessage(
        language === "es"
          ? `Suscripción de Mercado Pago creada en ${response.data.currency} ${Number(response.data.amount || 0).toLocaleString("es-CO")}`
          : `Mercado Pago subscription created in ${response.data.currency} ${Number(response.data.amount || 0).toLocaleString("en-US")}`
      );
      setStatusTone("success");
      await refreshUser();
      await loadData();
    } catch (error) {
      setStatusMessage(getBillingErrorMessage(error));
      setStatusTone("error");
    } finally {
      setMercadoLoading(false);
    }
  }, [getBillingErrorMessage, getMercadoPagoDeviceSessionId, language, loadData, mercadoPayerForm, refreshUser, selectedCountry, selectedPlan, t.choosePlan, t.mercadoPagoHostedRedirecting, t.mercadoPagoPayerValidation]);

  const resetCheckoutSelection = () => {
    setSelectedPlan(null);
    setStatusMessage("");
    setStatusTone("muted");
  };

  const changePlan = async (planId) => {
    setBusyPlan(planId);
    try {
      const response = await axios.post(`${API_URL}/api/subscription/change-plan`, {
        plan_id: planId,
        country_code: selectedCountry,
        origin_url: window.location.origin,
      });

      if (response.data?.requires_external_payment && response.data?.redirect_url) {
        setStatusMessage(response.data.message || t.processing);
        setStatusTone("muted");
        window.location.href = response.data.redirect_url;
        return;
      }

      setStatusMessage(response.data?.message || t.planUpdated);
      setStatusTone("success");
      await refreshUser();
      await loadData();
    } catch (error) {
      setStatusMessage(getBillingErrorMessage(error));
      setStatusTone("error");
    } finally {
      setBusyPlan(null);
    }
  };

  const cancelSubscription = async () => {
    setBusyPlan("cancel");
    try {
      await axios.post(`${API_URL}/api/subscription/cancel`);
      setStatusMessage(t.cancelScheduled);
      setStatusTone("success");
      await refreshUser();
      await loadData();
    } catch (error) {
      setStatusMessage(getBillingErrorMessage(error));
      setStatusTone("error");
    } finally {
      setBusyPlan(null);
    }
  };

  const getPlanAction = (plan) => {
    if (hasPlanAccess && subscription?.plan_id === plan.id) {
      return { label: t.current, action: null, disabled: true };
    }

    if (!hasBlockingBillingSubscription || subscription?.plan_id === "free") {
      if (checkoutFormVisible) {
        return {
          label: selectedPlan === plan.id ? t.selected : t.choosePlan,
          action: null,
          disabled: true,
        };
      }

      return {
        label: t.choosePlan,
        action: () => setSelectedPlan(plan.id),
        disabled: false,
      };
    }

    const targetPrice = plan.price;
    if (targetPrice > currentPrice) return { label: t.upgrade, action: () => changePlan(plan.id), disabled: false };
    if (targetPrice < currentPrice) return { label: t.downgrade, action: () => changePlan(plan.id), disabled: false };
    return { label: t.switchPlan, action: () => changePlan(plan.id), disabled: false };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="billing-loading-state">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>{t.loading}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-6 py-8" data-testid="billing-page">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/pricing")}
              className="mb-4 px-0 text-muted-foreground hover:text-foreground"
              data-testid="billing-back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t.back}
            </Button>
            <h1 className="text-4xl font-bold text-foreground">{t.title}</h1>
            <p className="mt-2 text-muted-foreground">{t.subtitle}</p>
          </div>
          <Link to="/pricing" data-testid="billing-view-pricing-link">
            <Button variant="outline">{t.manageOnPricing}</Button>
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
          <Card className="border-border bg-card p-6" data-testid="billing-current-subscription-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm uppercase tracking-[0.24em] text-muted-foreground">{t.currentPlan}</p>
                <h2 className="mt-2 text-3xl font-semibold text-foreground" data-testid="billing-current-plan-name">
                  {hasPlanAccess ? subscription?.plan_name : t.noPlan}
                </h2>
              </div>
              <Badge className={subscriptionStatusClasses} data-testid="billing-subscription-status-badge">
                {subscriptionStatusLabel}
              </Badge>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
              <div className="rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-subscription-status-panel">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{t.status}</p>
                <p className="mt-2 text-lg font-semibold text-emerald-300">{subscriptionStatusLabel}</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-subscription-period-panel">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  {subscription?.cancel_at_period_end ? t.endsOn : t.renewsOn}
                </p>
                <p className="mt-2 text-lg font-semibold text-foreground">{formatDate(subscription?.current_period_end, language)}</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-provider-panel">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{t.currentProvider}</p>
                <p className="mt-2 min-w-0 break-words text-base font-semibold leading-snug text-foreground md:text-lg">{providerLabel}</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-current-price-panel">
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{t.currentPrice}</p>
                <p className="mt-2 text-lg font-semibold text-foreground">
                  {subscription?.currency ? `${subscription.currency} ${Number(subscription.amount || 0).toLocaleString(language === "es" ? "es-CO" : "en-US")}` : "—"}
                </p>
              </div>
            </div>

            {accessOnly && (
              <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="billing-access-only-hint">
                {t.accessOnlyHint}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              {hasBillingSubscription && subscription?.plan_id !== "free" && !subscription?.cancel_at_period_end && subscription?.status !== "cancelled" && (
                <Button onClick={cancelSubscription} disabled={busyPlan === "cancel"} data-testid="billing-cancel-button">
                  {busyPlan === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  {t.cancel}
                </Button>
              )}
            </div>
          </Card>

          <Card className="border-border bg-card p-6" data-testid="billing-dedicated-checkout-card">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{t.paymentPanel}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t.paymentPanelText}</p>
              </div>
            </div>

            {statusMessage && (
              <div
                className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${
                  statusTone === "success"
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : statusTone === "error"
                    ? "border-red-500/40 bg-red-500/10 text-red-300"
                    : "border-border bg-muted/40 text-muted-foreground"
                }`}
                data-testid="billing-status-message"
              >
                {statusMessage}
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-provider-selector-panel">
              <div className="flex justify-center overflow-hidden">
                <div className="flex h-24 w-full items-center justify-center overflow-hidden" data-testid="billing-provider-mercadopago-logo-wrap">
                  <img
                    src={MERCADO_PAGO_LOGO_SRC}
                    alt="Mercado Pago"
                    className="h-20 w-auto object-contain"
                    data-testid="billing-provider-mercadopago-logo"
                  />
                </div>
              </div>
              <div className="mt-4 grid gap-2">
                <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="billing-country-select">
                  {t.country}
                </label>
                <select
                  id="billing-country-select"
                  className="h-11 rounded-xl border border-border bg-background px-3 text-foreground"
                  value={selectedCountry}
                  onChange={(event) => setSelectedCountry(event.target.value)}
                  disabled={checkoutFormVisible}
                  data-testid="billing-country-select"
                >
                  {(providers?.mercadopago?.countries || []).map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name} · {country.currency}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-border/70 bg-muted/30 p-4" data-testid="billing-shield-panel">
              <div className="flex items-center gap-2 text-foreground">
                <CreditCard className="h-4 w-4 text-primary" />
                <span className="font-medium">Mercado Pago</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{t.mercadoPagoHint}</p>
              <p className="mt-2 text-sm text-muted-foreground" data-testid="billing-mercadopago-hosted-hint">{t.mercadoPagoHostedHint}</p>
            </div>

            {checkoutFormVisible && (
              <div className="mt-4 rounded-2xl border border-border/70 bg-muted/20 p-4" data-testid="mercadopago-payer-form-panel">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{t.mercadoPagoPayerSectionTitle}</p>
                  <p className="text-sm text-muted-foreground" data-testid="mercadopago-payer-form-hint">{t.mercadoPagoPayerSectionHint}</p>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="mercadopago-payer-email-input">
                      {t.mercadoPagoPayerEmailLabel}
                    </label>
                    <Input
                      id="mercadopago-payer-email-input"
                      type="email"
                      value={mercadoPayerForm.email}
                      onChange={(event) => updateMercadoPayerField("email", event.target.value.slice(0, 254))}
                      data-testid="mercadopago-payer-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="mercadopago-payer-first-name-input">
                      {t.mercadoPagoPayerFirstNameLabel}
                    </label>
                    <Input
                      id="mercadopago-payer-first-name-input"
                      value={mercadoPayerForm.firstName}
                      onChange={(event) => updateMercadoPayerField("firstName", event.target.value.slice(0, 80))}
                      data-testid="mercadopago-payer-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="mercadopago-payer-last-name-input">
                      {t.mercadoPagoPayerLastNameLabel}
                    </label>
                    <Input
                      id="mercadopago-payer-last-name-input"
                      value={mercadoPayerForm.lastName}
                      onChange={(event) => updateMercadoPayerField("lastName", event.target.value.slice(0, 80))}
                      data-testid="mercadopago-payer-last-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="mercadopago-identification-type-select">
                      {t.mercadoPagoIdentificationTypeLabel}
                    </label>
                    <select
                      id="mercadopago-identification-type-select"
                      className="h-11 rounded-xl border border-border bg-background px-3 text-foreground"
                      value={mercadoPayerForm.identificationType}
                      onChange={(event) => updateMercadoPayerField("identificationType", normalizeMercadoIdentificationType(event.target.value))}
                      data-testid="mercadopago-identification-type-select"
                    >
                      {mercadoIdentificationOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground" htmlFor="mercadopago-identification-number-input">
                      {t.mercadoPagoIdentificationNumberLabel}
                    </label>
                    <Input
                      id="mercadopago-identification-number-input"
                      value={mercadoPayerForm.identificationNumber}
                      onChange={(event) => updateMercadoPayerField("identificationNumber", normalizeMercadoIdentificationNumber(event.target.value))}
                      placeholder={t.mercadoPagoIdentificationNumberPlaceholder}
                      data-testid="mercadopago-identification-number-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-border/70 bg-muted/20 p-4" data-testid="billing-selected-plan-panel">
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{t.selectedPlan}</p>
              <div className="mt-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-foreground">{selectedPlanData?.name || "—"}</p>
                  <p className="text-sm text-muted-foreground">
                    {mercadoQuote
                      ? `${mercadoQuote.currency} ${Number(mercadoQuote.amount).toLocaleString(language === "es" ? "es-CO" : "en-US")}`
                      : selectedPlanData
                      ? `USD ${Number(selectedPlanData.price).toFixed(2)}`
                      : "—"}
                  </p>
                  {selectedPlanContent?.summary && (
                    <p className="mt-3 text-sm leading-6 text-muted-foreground" data-testid="billing-selected-plan-summary">
                      {selectedPlanContent.summary[language] || selectedPlanContent.summary.en}
                    </p>
                  )}
                </div>
                {selectedPlanData && <Badge data-testid="billing-selected-plan-badge">{selectedPlanData.id}</Badge>}
              </div>
              {checkoutFormVisible && (
                <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-border/60 bg-background/60 p-3 md:flex-row md:items-center md:justify-between">
                  <p className="text-sm text-muted-foreground" data-testid="billing-selection-locked-hint">
                    {t.selectionLockedHint}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetCheckoutSelection}
                    data-testid="billing-reset-selection-button"
                  >
                    {t.resetSelection}
                  </Button>
                </div>
              )}
            </div>

            {!hasBlockingBillingSubscription && selectedPlan && providers?.mercadopago?.enabled && mercadoQuote && (
              <div className="mt-4">
                <div className="rounded-2xl border border-border/70 bg-muted/20 p-4" data-testid="mercadopago-hosted-redirect-panel">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Mercado Pago</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {mercadoQuote
                          ? `${mercadoQuote.currency} ${Number(mercadoQuote.amount).toLocaleString(language === "es" ? "es-CO" : "en-US")}`
                          : "—"}
                      </p>
                    </div>
                    <Badge data-testid="mercadopago-hosted-country-badge">{selectedCountry}</Badge>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground" data-testid="mercadopago-hosted-redirect-copy">
                    {t.mercadoPagoHostedHint}
                  </p>
                  <Button
                    className="mt-4 w-full"
                    onClick={startMercadoPagoCheckout}
                    disabled={mercadoLoading}
                    data-testid="mercadopago-hosted-redirect-button"
                  >
                    {mercadoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                    {t.mercadoPagoHostedButton}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {plans.map((plan) => {
            const action = getPlanAction(plan);
            const planContent = billingPlanContent[plan.id] || null;
            const translatedFeatures = planContent?.features?.[language] || planContent?.features?.en || [];
            const planSummary = planContent?.summary?.[language] || planContent?.summary?.en || "";
            const localQuote = providers?.mercadopago?.quotes?.[selectedCountry]?.[plan.id];
            return (
              <Card key={plan.id} className="border-border bg-card p-6" data-testid={`billing-plan-card-${plan.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{plan.name}</p>
                    <div className="mt-3 flex items-end gap-1 text-foreground">
                      <span className="text-lg">$</span>
                      <span className="text-4xl font-bold">{Number(plan.price).toFixed(2)}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">/ month</p>
                    {localQuote && (
                      <p className="mt-2 text-sm text-primary" data-testid={`billing-plan-local-quote-${plan.id}`}>
                        {localQuote.currency} {Number(localQuote.amount).toLocaleString(language === "es" ? "es-CO" : "en-US")}
                      </p>
                    )}
                    {planSummary ? (
                      <p className="mt-3 text-sm leading-6 text-muted-foreground" data-testid={`billing-plan-summary-${plan.id}`}>
                        {planSummary}
                      </p>
                    ) : null}
                  </div>
                  {subscription?.plan_id === plan.id && <Badge data-testid={`billing-current-plan-badge-${plan.id}`}>{t.current}</Badge>}
                </div>

                <div className="mt-6 rounded-2xl border border-border/70 bg-muted/20 p-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{t.included}</p>
                  <ul className="mt-3 space-y-3 text-sm text-foreground">
                    {translatedFeatures.map((feature) => (
                      <li key={`${plan.id}-${feature.text}`} className={`flex items-start gap-2 ${feature.included ? "text-foreground" : "text-muted-foreground/70"}`}>
                        {feature.included ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-400" /> : <X className="mt-0.5 h-4 w-4" />}
                        <span className="leading-6">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  className="mt-6 w-full"
                  onClick={action.action || undefined}
                  disabled={action.disabled || busyPlan === plan.id}
                  data-testid={`billing-plan-action-${plan.id}`}
                >
                  {busyPlan === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : action.label}
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
